import { employeeVisibleComments } from 'tio-common/utils/tuition-assistance/comments';
import type { CommentExtension as CommentModel } from 'tio-data/data/resources/comment.ext';

export {
  employeeVisibleComments,
  EMPLOYEE_VISIBLE_COMMENT_TAGS,
} from 'tio-common/utils/tuition-assistance/comments';

/**
 * How many employee-visible comments the employee has not seen and did not write.
 *
 * There is no server-side read receipt on comments — the model carries
 * `comment/commentor/createdAt/updatedAt/tags/commentable/person` and nothing else
 * — so read state is held client-side, as the set of comment ids that were on
 * screen the last time the employee opened the comments drawer. See
 * ManagePage#commentsSeenIds.
 *
 * Ids rather than a timestamp, deliberately. `createdAt` is stamped by the
 * AUTHOR's browser (`content.gts` does `createdAt: new Date().toISOString()` at
 * both creation sites), while any timestamp we persisted would come from the
 * VIEWER's browser. Comparing the two means comparing two machines' wall clocks:
 * an employee whose clock runs ahead would have approver comments arrive already
 * "read" and never see a badge for them — a silent, permanent miss, which is the
 * one failure this badge exists to prevent. Ids are assigned server-side and need
 * no clock at all.
 *
 * `currentPersonId` excludes the employee's own comments: they know what they just
 * wrote. Ownership is matched on `person.id`, the same key `content.gts`'s
 * `canEditComment` uses — `commentor` is only a display name and can collide.
 * When the id is absent (session unresolved, or the unsaved fallback record
 * `sessionContext.person` returns) nothing is excluded: we can't attribute
 * authorship, so we'd rather show a badge than hide someone else's message.
 *
 * An empty or missing seen-set counts EVERYTHING as unread rather than nothing.
 * On first visit after release an employee with old comments sees a badge that
 * clears the moment they open the drawer — self-correcting noise. Seeding "all
 * current ids" instead would be quieter but would silently swallow a comment
 * nobody had actually read.
 */
export function unreadCommentCount(
  comments: CommentModel[] | undefined | null,
  seenIds: ReadonlySet<string> | null,
  currentPersonId?: string | null
): number {
  return employeeVisibleComments(comments).filter((comment) => {
    if (currentPersonId && comment.person?.id === currentPersonId) {
      return false;
    }
    // No id means nothing could have recorded it as seen, so it counts. (In
    // practice `employeeVisibleComments` has already dropped unsaved records, which
    // are the only ones without one.)
    return comment.id ? !seenIds?.has(comment.id) : true;
  }).length;
}

/** Ids to persist when the drawer opens: everything the employee could see at that
 * moment, own comments included, so the stored set is a faithful record of what was
 * on screen rather than a filtered view of it. */
export function visibleCommentIds(comments: CommentModel[] | undefined | null): string[] {
  return employeeVisibleComments(comments)
    .map((comment) => comment.id)
    .filter((id): id is string => Boolean(id));
}

/** localStorage key holding the comment ids this employee has seen for one
 * application. Scoped per application so reading one request's thread does not
 * clear the badge on another. */
export function commentsSeenStorageKey(applicationId: string): string {
  return `tas-manage-comments-seen:${applicationId}`;
}

/** Parses the stored id list, tolerating anything that isn't the shape we wrote —
 * a corrupt or hand-edited value degrades to "nothing seen", which shows a badge
 * rather than hiding one. */
export function parseSeenCommentIds(raw: string | null): ReadonlySet<string> | null {
  if (!raw) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return null;
    }
    return new Set(parsed.filter((id): id is string => typeof id === 'string'));
  } catch {
    return null;
  }
}
